
import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAppDispatch } from '@/store/hooks';
import { register } from '@/store/authSlice';
import { organizationAPI, Organization } from '@/api/organizationAPI';
import { roleAPI, RoleDTO } from '@/api/roleAPI';
import { UserPlus, Loader, Eye, EyeOff } from 'lucide-react';
import toast from 'react-hot-toast';

interface FormErrors {
  username?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  password?: string;
  confirmPassword?: string;
  organization?: string;
}

export const Register = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const [username, setUsername] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [organizationId, setOrganizationId] = useState('');
  const [roleId, setRoleId] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errors, setErrors] = useState<FormErrors>({});
  
  // Organizations list
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [loadingOrgs, setLoadingOrgs] = useState(false);
  const [roles, setRoles] = useState<RoleDTO[]>([]);
  const [loadingRoles, setLoadingRoles] = useState(false);

  // Fetch organizations on mount
  useEffect(() => {
    const fetchOrganizations = async () => {
      try {
        setLoadingOrgs(true);
        const orgs = await organizationAPI.getOrganizations({ limit: 100 });
        setOrganizations(orgs);
      } catch (err: any) {
        console.error('Failed to load organizations:', err);
        toast.error('Failed to load organizations');
      } finally {
        setLoadingOrgs(false);
      }
    };
    fetchOrganizations();
    const fetchRoles = async () => {
      try {
        setLoadingRoles(true);
        const data = await roleAPI.getPublicRoles();
        setRoles(data);
        // default to first entry if exists
        if (data && data.length > 0) setRoleId(data[0].id);
      } catch (err: any) {
        console.error('Failed to load roles:', err);
        toast.error('Failed to load roles');
      } finally {
        setLoadingRoles(false);
      }
    };
    fetchRoles();
  }, []);

  // Client-side validation
  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

  if (!username.trim()) newErrors.username = 'Username is required';
  else if (username.length < 3) newErrors.username = 'Username must be at least 3 characters';
  else if (username.length > 50) newErrors.username = 'Username must be less than 50 characters';
  else if (/^\d+$/.test(username)) newErrors.username = 'Username cannot be only numbers';
  else if (!/^[a-zA-Z0-9_.\s]+$/.test(username))
    newErrors.username = 'Username can only contain letters, numbers, underscores, spaces, and dots';


    if (!firstName.trim()) newErrors.firstName = 'First name is required';
    else if (firstName.length < 2) newErrors.firstName = 'First name must be at least 2 characters';
    else if (firstName.length > 100) newErrors.firstName = 'First name must be less than 100 characters';
    else if (!/^[a-zA-Z\s'-]+$/.test(firstName)) newErrors.firstName = 'First name can only contain letters, spaces, hyphens, and apostrophes';

    if (!lastName.trim()) newErrors.lastName = 'Last name is required';
    else if (lastName.length < 2) newErrors.lastName = 'Last name must be at least 2 characters';
    else if (lastName.length > 100) newErrors.lastName = 'Last name must be less than 100 characters';
    else if (!/^[a-zA-Z\s'-]+$/.test(lastName)) newErrors.lastName = 'Last name can only contain letters, spaces, hyphens, and apostrophes';

    if (!email.trim()) newErrors.email = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) newErrors.email = 'Please enter a valid email address';

    if (!password) newErrors.password = 'Password is required';
    else if (password.length < 8) newErrors.password = 'Password must be at least 8 characters';
    else if (password.length > 72) newErrors.password = 'Password must be less than 72 characters';
    else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(password)) newErrors.password = 'Password must contain uppercase, lowercase, and a number';

    if (!confirmPassword) newErrors.confirmPassword = 'Please confirm your password';
    else if (password !== confirmPassword) newErrors.confirmPassword = 'Passwords do not match';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    const payload: any = {
      username: username.trim(),
      email: email.trim().toLowerCase(),
      password,
      first_name: firstName.trim(),
      last_name: lastName.trim(),
    };

    // Add organization_id if selected
    if (organizationId) {
      payload.organization_id = organizationId;
    }
    if (roleId) {
      payload.role_id = roleId;
    }

    try {
      setLoading(true);
      await dispatch(register(payload)).unwrap();
      toast.success('Account created successfully! Please login.');
      navigate('/login'); // only on success
    } catch (err: any) {
      // Map backend errors to fields
      if (err?.field && err?.message) {
        setErrors(prev => ({ ...prev, [err.field]: err.message }));
      } else if (err?.message) {
        if (err.message.toLowerCase().includes('username')) setErrors(prev => ({ ...prev, username: err.message }));
        else if (err.message.toLowerCase().includes('email')) setErrors(prev => ({ ...prev, email: err.message }));
        else setErrors(prev => ({ ...prev, username: err.message }));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: keyof FormErrors, value: string) => {
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: undefined }));
    switch (field) {
      case 'username': setUsername(value); break;
      case 'firstName': setFirstName(value); break;
      case 'lastName': setLastName(value); break;
      case 'email': setEmail(value); break;
      case 'password': setPassword(value); break;
      case 'confirmPassword': setConfirmPassword(value); break;
    }
  };

  const renderInput = (
    id: keyof FormErrors,
    label: string,
    value: string,
    type = 'text',
    placeholder = '',
    showToggle?: boolean,
    toggleValue?: boolean,
    onToggle?: () => void
  ) => (
    <div>
      <label htmlFor={id} className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      <div className="relative">
        <input
          id={id}
          type={type}
          value={value}
          placeholder={placeholder}
          onChange={(e) => handleInputChange(id, e.target.value)}
          className={`input w-full pr-${showToggle ? '10' : '2'} ${errors[id] ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''}`}
        />
        {showToggle && toggleValue !== undefined && onToggle && (
          <button 
            type="button" 
            tabIndex={-1}
            className="absolute inset-y-0 right-0 pr-3 flex items-center z-10" 
            onClick={onToggle}
            aria-label={toggleValue ? 'Hide password' : 'Show password'}
          >
            {toggleValue ? <EyeOff className="h-4 w-4 text-gray-400" /> : <Eye className="h-4 w-4 text-gray-400" />}
          </button>
        )}
      </div>
      {errors[id] && <p className="mt-1 text-sm text-red-600">{errors[id]}</p>}
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 to-primary-100 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-8">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-primary-600 rounded-full mb-4">
            <UserPlus className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900">Create Account</h1>
          <p className="text-gray-600 mt-2">Sign up to get started</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {renderInput('username', 'Username', username, 'text', 'johndoe')}
          <div className="grid grid-cols-2 gap-4">
            {renderInput('firstName', 'First Name', firstName, 'text', 'John')}
            {renderInput('lastName', 'Last Name', lastName, 'text', 'Doe')}
          </div>
          {renderInput('email', 'Email', email, 'text', 'your@email.com')}
          <div>
            <label htmlFor="role" className="block text-sm font-medium text-gray-700 mb-1">Role</label>
            <select
              id="role"
              value={roleId}
              onChange={(e) => setRoleId(e.target.value)}
              className="input w-full"
              disabled={loadingRoles}
              required
            >
              {roles.map((r) => (
                <option key={r.id} value={r.id}>{r.display_name || r.name}</option>
              ))}
            </select>
            {roles.length === 0 && !loadingRoles && (
              <p className="mt-1 text-xs text-gray-500">No roles available</p>
            )}
          </div>
          
          <div>
            <label htmlFor="organization" className="block text-sm font-medium text-gray-700 mb-1">
              Organization (Optional)
            </label>
            <select
              id="organization"
              value={organizationId}
              onChange={(e) => setOrganizationId(e.target.value)}
              className="input w-full"
              disabled={loadingOrgs}
            >
              <option value="">Select organization...</option>
              {organizations.map((org) => (
                <option key={org.id} value={org.id}>
                  {org.name}
                </option>
              ))}
            </select>
            {organizations.length === 0 && !loadingOrgs && (
              <p className="mt-1 text-xs text-gray-500">No organizations available</p>
            )}
          </div>
          
          {renderInput('password', 'Password', password, showPassword ? 'text' : 'password', '••••••••', true, showPassword, () => setShowPassword(!showPassword))}
          {renderInput('confirmPassword', 'Confirm Password', confirmPassword, showConfirmPassword ? 'text' : 'password', '••••••••', true, showConfirmPassword, () => setShowConfirmPassword(!showConfirmPassword))}

          <button type="submit" disabled={loading} className="btn btn-primary w-full flex items-center justify-center gap-2">
            {loading ? <><Loader className="w-5 h-5 animate-spin" />Creating account...</> : <><UserPlus className="w-5 h-5" />Sign Up</>}
          </button>
        </form>

        <div className="mt-6 text-center">
          <p className="text-sm text-gray-600">
            Already have an account?{' '}
            <Link to="/login" className="text-primary-600 hover:text-primary-700 font-medium">Sign in</Link>
          </p>
        </div>
      </div>
    </div>
  );
};
 
